/**
 * This file is responsible for storing the preferences to do with meditation times.
 * It memorises custom settings and sliders and saves them according to the patterns.
 * For example, if two days offer 3 or 4 meditations, whatever number you choose will
 * be remembered for another day that offers 3 or 4 meditations. This is done via
 * what's referred to as a signature.
 *
 * A generator can create a LessonTime, for example a lesson might specify you do
 * it 3-5 times in a day. You can choose 3, 4, or 5. Within that, you can choose
 * WHEN you want to do these. This means for example that one generator generates
 * a LessonTime which, if it's a 3-version, has 3 moveable pieces.
 *
 *  Generator -> LessonTime -> Arrangement
 *
 * All of this needs to be saved locally I think, otherwise it may get complicated...
 * Usually it's only on one device anyway
 */
//

import {
    type LessonTime,
    type PerDayLessonGenerator,
    type PerHourLessonGenerator,
} from "./reminder-time-conversion"
import { simpleHash } from "@utils/utilities/simple-hash"
import { derived, writable } from "svelte/store"
import type { MinutesAfterMidnight } from "./day-time"
import { clamp, difference, intersection, pull, union } from "lodash-es"
import { user } from "@utils/underhood/firebase"
import type { User } from "firebase/auth"
import { Channel } from "@utils/cap-notification-manager/cap-notification-manager"

type MemoryData = {
    /**
     * Contains a map of timehashes and the appropriate
     * arrangements for the associated lessons
     */
    timeHashMap: Record<TimeHash, Arrangement>
    /**
     * Basically saves preferences for generators, which can generate different {@link LessonTime}s.
     * So for example you might have a lesson that can have 3-6 per day. And you can choose.
     */
    generatorMap: Record<GeneratorSignature, number | undefined>
    /**
     * Collection of channels that are disabled
     */
    channelsDisabled: Channel[]
}

function generateDefault(): MemoryData {
    return {
        timeHashMap: {},
        generatorMap: {},
        channelsDisabled: [Channel.TEMPTATION],
    }
}

/**
 * You need to access this with reactive $ value
 * to get live updates.
 *
 * Make sure to use $ on this to extract the store and get live updates.
 * Otherwise it will be non-reactive.
 */
export class ReminderMemoryManagerSettings {
    data: MemoryData
    constructor(public readonly key: string) {
        const stored = localStorage.getItem(key)
        if (!stored) {
            console.log(`🔑 ${key} - Nothing stored, setting default`)
            this.data = generateDefault()
        } else {
            console.log(`🔑 ${key} - Retrieving from store`)
            this.data = JSON.parse(stored)
        }
    }

    atLeastOneChannelEnabled(
        outOf: Channel[] = [
            Channel.FREQUENT_REMINDERS,
            Channel.HOURLY_REMEMBRANCE,
            Channel.LESSON_REMINDER,
            Channel.MAIN_PRACTICE,
            Channel.TEMPTATION,
        ],
    ) {
        const intersected = intersection(outOf, this.channelsEnabled)
        return intersected.length > 0
    }

    get channelsEnabled() {
        const allChannels = [
            Channel.FREQUENT_REMINDERS,
            Channel.HOURLY_REMEMBRANCE,
            Channel.LESSON_REMINDER,
            Channel.MAIN_PRACTICE,
            Channel.TEMPTATION,
        ]
        return difference(allChannels, this.data.channelsDisabled)
    }

    #store = writable<ReminderMemoryManagerSettings>(this)
    readonly subscribe = this.#store.subscribe

    /**
     * Get the array of the lesson time merging with reactive store
     * @param lessonTime
     * @param bottomLimit
     * @param topLimit
     * @returns
     */
    getLessonTime(
        lessonTime: LessonTime,
        bottomLimit: MinutesAfterMidnight,
        topLimit: MinutesAfterMidnight,
    ) {
        const minutes = lessonTime.getMinutes(bottomLimit, topLimit)

        // See if we have stored data
        const arrangement = this.data.timeHashMap[practiceLessonTimeSignature(lessonTime)]
        if (arrangement === undefined) return minutes

        // There is a pre-set arrangement, merge
        return minutes.map((defaultMinute, meditationIndex) => {
            const customMinute = arrangement[meditationIndex]
            return customMinute === undefined ? defaultMinute : clamp(customMinute, bottomLimit, topLimit)
        })
    }

    /**
     * Retrieve the current lesson given the current settings or default.
     * @param lessonTime
     * @param wakeup
     * @param sleep
     * @returns
     */
    getLessonTimeObject(
        lessonTime: LessonTime,
        wakeup: MinutesAfterMidnight,
        sleep: MinutesAfterMidnight,
    ) {
        return {...this.getLessonTime(lessonTime, wakeup, sleep)} as Arrangement
    }

    /**
     * Updates the user's preferred configuration time for a specific
     * individual reminder of a specific practice
     *
     * @param lessonTime
     * @param id
     * @param minute
     */
    updateTimeLesson(lessonTime: LessonTime, id: MeditationIndex, minute: number) {
        const signature = practiceLessonTimeSignature(lessonTime)

        let arrangement = this.data.timeHashMap[signature]
        if (arrangement === undefined) arrangement = {}

        // Update a position for one particular one
        arrangement[id] = minute
        this.data.timeHashMap[signature] = arrangement

        this.update()
    }

    /**
     * Gets the pre-set for this generator or default
     * @param generator
     * @returns
     */
    getGeneration(generator: PerHourLessonGenerator | PerDayLessonGenerator) {
        const storedOption = this.data.generatorMap[generator.signature]
        return storedOption && generator.options.has(storedOption)
            ? storedOption
            : generator.defaultOption
    }

    /**
     * Sets a generator option
     * @param generator
     * @param value
     * @throws if its a bad option
     */
    updateGeneratorOption(
        generator: PerHourLessonGenerator | PerDayLessonGenerator,
        value: number,
    ) {
        if (!generator.options.has(value))
            throw new Error(`Bad value ${value}! Not in stored option`)
        this.data.generatorMap[generator.signature] = value

        this.update()
    }

    channelIsEnabled(channel: Channel) {
        return !this.data.channelsDisabled.includes(channel)
    }

    setChannel(channel: Channel, enabled: boolean) {
        if (enabled) {
            pull(this.data.channelsDisabled, channel)
        } else {
            this.data.channelsDisabled = union(this.data.channelsDisabled, [channel])
        }

        this.update()
    }

    /**
     * Restores defaults and saves to disk, also informs subs
     */
    restoreDefaults() {
        this.data = generateDefault()
        this.update()
    }

    /**
     * Updates the store for this instance and saves to disk
     */
    update() {
        this.#store.update((r) => r)
        this.saveLocal()
    }

    saveLocal() {
        localStorage.setItem(this.key, JSON.stringify(this.data))
    }
}

function generateKeyFromUser(user: User) {
    return "reminder-memory-" + user.uid
}

export const reminderMemoryManagerSettings = derived(user, (user) => {
    if (user === null) return null
    return new ReminderMemoryManagerSettings(generateKeyFromUser(user))
})

/**
 * Generate a unique signature for a particular {@link LessonTime}. This allows
 * lessons of the same configuration to have the same signature, allowing
 * for memorisation
 * @param lessonTime
 * @returns
 */
function practiceLessonTimeSignature(lessonTime: LessonTime): TimeHash {
    const rtn =
        lessonTime.individualMovability +
        "_" +
        lessonTime.collectiveMovability +
        "_" +
        lessonTime.getMinutes(0, 2400).join()
    return simpleHash(rtn)
}

type TimeHash = string

/**
 * The individual index of a {@link LessonTime}'s individual reminder. Only
 * really for exact reminders.
 */
type MeditationIndex = number

/**
 * An arrangement is basically the layout of a LessonTime
 * so for example if you have a LessonTime of 3,
 * then you have
 *
 * ```
 * {
 *  1. 600 // 10am or whatever
 *  2. 1200 // ...
 *  3. etc // ...
 * }
 * ```
 */
type Arrangement = {
    [key in MeditationIndex]: MinutesAfterMidnight
}
type GeneratorSignature = string
